import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ChevronLeft, Plus, Pencil, Trash2, ChevronUp, ChevronDown } from 'lucide-react';
import toast from 'react-hot-toast';
import { useAuth } from '../../../AuthProvider';
import { supabase } from '../../../lib/supabase';
import ParcoursStatusBadge from '../../../components/admin/ParcoursStatusBadge';

type StepStatus = 'draft' | 'published';

interface Step {
  id: string;
  parcours_id: string;
  title: string;
  step_id: string;
  description: string | null;
  icon: string | null;
  order_index: number;
  status: StepStatus;
  blocks: unknown[];
}

interface ParcoursParent {
  id: string;
  company_name: string;
  slug: string;
  status: string;
}

function generateStepId(title: string): string {
  return title
    .toLowerCase()
    .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
}

export default function ParcoursStepsAdmin() {
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();
  const navigate = useNavigate();

  const [parcours, setParcours] = useState<ParcoursParent | null>(null);
  const [steps, setSteps] = useState<Step[]>([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [userRole, setUserRole] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);

  useEffect(() => {
    async function init() {
      if (!user) { navigate('/'); return; }
      const { data: ud } = await supabase.from('users').select('role').eq('id', user.id).maybeSingle();
      const role = ud?.role ?? null;
      if (role !== 'admin' && role !== 'editor') { navigate('/'); return; }
      setUserRole(role);
      await load();
    }
    init();
  }, [user?.id, id]);

  async function load() {
    if (!id) return;
    try {
      setLoading(true);

      const { data: p, error: pe } = await supabase
        .from('parcours')
        .select('id, company_name, slug, status')
        .eq('id', id)
        .maybeSingle();

      if (pe) throw pe;
      if (!p) { setNotFound(true); setLoading(false); return; }
      setParcours(p);

      const { data: s, error: se } = await supabase
        .from('parcours_steps')
        .select('*')
        .eq('parcours_id', id)
        .order('order_index', { ascending: true });

      if (se) throw se;
      setSteps(
        (s ?? []).map((row) => ({
          ...row,
          blocks: Array.isArray(row.blocks) ? row.blocks : [],
        }))
      );
    } catch {
      toast.error('Impossible de charger les étapes.');
    } finally {
      setLoading(false);
    }
  }

  async function handleAdd() {
    if (!id || adding) return;
    setAdding(true);
    const toastId = toast.loading('Création de l\'étape...');
    try {
      const baseTitle = 'Nouvelle étape';
      const baseStepId = generateStepId(baseTitle);

      // Trouver un step_id unique pour ce parcours
      const existingIds = steps.map((s) => s.step_id);
      let slug = baseStepId;
      let attempt = 1;
      while (existingIds.includes(slug)) {
        attempt++;
        slug = `${baseStepId}-${attempt}`;
      }

      const maxOrder = steps.reduce((m, s) => Math.max(m, s.order_index), 0);

      const { data, error } = await supabase
        .from('parcours_steps')
        .insert({
          parcours_id: id,
          title: baseTitle,
          step_id: slug,
          order_index: maxOrder + 1,
          status: 'draft',
          blocks: [],
        })
        .select('id')
        .single();

      if (error) throw error;
      toast.success('Étape créée.', { id: toastId });
      navigate(`/admin/parcours/${id}/steps/${data.id}/edit`);
    } catch {
      toast.error('Impossible de créer l\'étape.', { id: toastId });
    } finally {
      setAdding(false);
    }
  }

  async function handleMove(step: Step, direction: 'up' | 'down') {
    const sorted = [...steps].sort((a, b) => a.order_index - b.order_index);
    const idx = sorted.findIndex((s) => s.id === step.id);
    const swapIdx = direction === 'up' ? idx - 1 : idx + 1;
    if (swapIdx < 0 || swapIdx >= sorted.length) return;

    const other = sorted[swapIdx];
    const toastId = toast.loading('Déplacement...');
    try {
      const { error: e1 } = await supabase
        .from('parcours_steps')
        .update({ order_index: other.order_index })
        .eq('id', step.id);
      if (e1) throw e1;

      const { error: e2 } = await supabase
        .from('parcours_steps')
        .update({ order_index: step.order_index })
        .eq('id', other.id);
      if (e2) throw e2;

      toast.success('Ordre mis à jour.', { id: toastId });
      await load();
    } catch {
      toast.error('Impossible de déplacer l\'étape.', { id: toastId });
    }
  }

  async function handleDelete(step: Step) {
    if (!confirm(`Supprimer l'étape "${step.title}" ? Cette action est irréversible.`)) return;
    const toastId = toast.loading('Suppression...');
    try {
      const { error } = await supabase.from('parcours_steps').delete().eq('id', step.id);
      if (error) throw error;
      toast.success('Étape supprimée.', { id: toastId });
      await load();
    } catch {
      toast.error('Impossible de supprimer l\'étape.', { id: toastId });
    }
  }

  // ── Rendu ─────────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-pink-600 border-t-transparent" />
      </div>
    );
  }

  if (notFound || !parcours) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Helmet><meta name="robots" content="noindex, nofollow" /></Helmet>
        <div className="mx-auto max-w-4xl px-6 py-24 text-center">
          <p className="text-5xl font-bold text-slate-200 mb-4">404</p>
          <h1 className="text-2xl font-semibold text-slate-800 mb-2">Parcours introuvable</h1>
          <p className="text-slate-500 mb-8">Ce parcours n'existe pas ou a été supprimé.</p>
          <Link to="/admin/parcours" className="inline-flex items-center gap-2 text-sm font-medium text-pink-600 hover:text-pink-700">
            <ChevronLeft className="h-4 w-4" /> Retour à la liste
          </Link>
        </div>
      </div>
    );
  }

  const sorted = [...steps].sort((a, b) => a.order_index - b.order_index);

  return (
    <div className="min-h-screen bg-slate-50">
      <Helmet><meta name="robots" content="noindex, nofollow" /></Helmet>

      <div className="mx-auto max-w-5xl px-6 py-8">

        {/* Header */}
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-3">
            <Link
              to={`/admin/parcours/${id}/edit`}
              className="rounded-lg p-1.5 text-slate-500 hover:bg-slate-200 transition-colors"
            >
              <ChevronLeft className="h-5 w-5" />
            </Link>
            <div>
              <h1 className="text-xl font-bold text-slate-900">
                Étapes — {parcours.company_name}
              </h1>
              <p className="text-xs text-slate-500 mt-0.5">
                {sorted.length} étape{sorted.length !== 1 ? 's' : ''}
                {' · '}
                <Link to={`/admin/parcours/${id}/edit`} className="hover:text-slate-800 underline underline-offset-2">
                  Modifier le parcours
                </Link>
              </p>
            </div>
          </div>

          <button
            onClick={handleAdd}
            disabled={adding}
            className="inline-flex items-center gap-2 rounded-xl bg-pink-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-pink-700 transition-colors disabled:opacity-50"
          >
            <Plus className="h-4 w-4" />
            Ajouter une étape
          </button>
        </div>

        {/* Table */}
        <div className="mt-6 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
          {sorted.length === 0 ? (
            <div className="py-20 text-center">
              <p className="text-slate-500 text-sm mb-4">Aucune étape pour ce parcours.</p>
              <button
                onClick={handleAdd}
                disabled={adding}
                className="inline-flex items-center gap-1.5 text-sm font-medium text-pink-600 hover:text-pink-700"
              >
                <Plus className="h-4 w-4" /> Créer la première étape
              </button>
            </div>
          ) : (
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-100 text-left">
                  <th className="w-12 px-4 py-3.5 text-xs font-semibold uppercase tracking-wider text-slate-500">#</th>
                  <th className="px-5 py-3.5 text-xs font-semibold uppercase tracking-wider text-slate-500">Titre</th>
                  <th className="hidden px-5 py-3.5 text-xs font-semibold uppercase tracking-wider text-slate-500 sm:table-cell">Identifiant</th>
                  <th className="px-5 py-3.5 text-xs font-semibold uppercase tracking-wider text-slate-500">Statut</th>
                  <th className="hidden px-5 py-3.5 text-xs font-semibold uppercase tracking-wider text-slate-500 md:table-cell">Blocs</th>
                  <th className="px-5 py-3.5 text-right text-xs font-semibold uppercase tracking-wider text-slate-500">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {sorted.map((step, idx) => (
                  <tr key={step.id} className="hover:bg-slate-50 transition-colors">
                    <td className="px-4 py-4 text-sm font-medium text-slate-400 text-center">
                      {step.order_index}
                    </td>
                    <td className="px-5 py-4">
                      <p className="font-medium text-slate-900 text-sm">{step.title}</p>
                    </td>
                    <td className="hidden px-5 py-4 sm:table-cell">
                      <code className="rounded bg-slate-100 px-1.5 py-0.5 text-xs text-slate-600">
                        {step.step_id}
                      </code>
                    </td>
                    <td className="px-5 py-4">
                      <ParcoursStatusBadge status={step.status} />
                    </td>
                    <td className="hidden px-5 py-4 text-sm text-slate-500 md:table-cell">
                      {step.blocks.length}
                    </td>
                    <td className="px-5 py-4">
                      <div className="flex items-center justify-end gap-1">
                        {/* Monter */}
                        <button
                          onClick={() => handleMove(step, 'up')}
                          disabled={idx === 0}
                          title="Monter"
                          className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700 transition-colors disabled:opacity-25 disabled:cursor-not-allowed"
                        >
                          <ChevronUp className="h-4 w-4" />
                        </button>
                        {/* Descendre */}
                        <button
                          onClick={() => handleMove(step, 'down')}
                          disabled={idx === sorted.length - 1}
                          title="Descendre"
                          className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700 transition-colors disabled:opacity-25 disabled:cursor-not-allowed"
                        >
                          <ChevronDown className="h-4 w-4" />
                        </button>
                        {/* Modifier */}
                        <Link
                          to={`/admin/parcours/${id}/steps/${step.id}/edit`}
                          title="Modifier"
                          className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700 transition-colors"
                        >
                          <Pencil className="h-4 w-4" />
                        </Link>
                        {/* Supprimer */}
                        {userRole === 'admin' && (
                          <button
                            onClick={() => handleDelete(step)}
                            title="Supprimer"
                            className="rounded-lg p-1.5 text-slate-400 hover:bg-red-50 hover:text-red-600 transition-colors"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

      </div>
    </div>
  );
}
